import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Clock, LogIn, LogOut, Timer, RefreshCw, ChevronDown, CalendarDays, Pencil, Trash2, X, Check, History, Download, Plus, Coffee } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import TimeClockChangelogDialog from "../components/TimeClockChangelogDialog";
import KioskPairingCard from "../components/KioskPairingCard";
import MissedPunchManager from "../components/MissedPunchManager";
import TimeOffManager from "../components/TimeOffManager";
import DateRangeFilter, { computeRange } from "../components/DateRangeFilter";
import AddShiftModal from "../components/AddShiftModal";

const formatTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

// Convert a decimal-hours value (e.g., 2.57) or seconds-diff to "Xh YYm"
const formatHoursMinutes = (hoursDecimal) => {
  if (hoursDecimal == null || isNaN(hoursDecimal)) return "0h 00m";
  const totalMinutes = Math.max(0, Math.round(hoursDecimal * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const msBetween = (startIso, endIso) => {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, end - start);
};

const formatElapsed = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
};

// Convert ISO datetime to local "YYYY-MM-DDTHH:MM" for datetime-local inputs
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert local "YYYY-MM-DDTHH:MM" back to ISO with timezone
const localInputToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function TimeClockPage({ embedded = false }) {
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "manager";
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userLog, setUserLog] = useState({}); // { userId: entries[] }
  const [now, setNow] = useState(Date.now());
  const [editingEntry, setEditingEntry] = useState(null); // entry_id being edited
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", notes: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  // Tracks which staff member the "Add manual shift" modal is open for.
  // Managers can now add a shift inline from any team card instead of
  // bouncing over to the Payroll / Staff pages.
  const [addShiftFor, setAddShiftFor] = useState(null);

  // Date-range filter state. Default: This week (matches prior behavior).
  const [dateFilter, setDateFilter] = useState(() => {
    const r = computeRange("this_week");
    return { preset: "this_week", customStart: null, customEnd: null, ...r };
  });

  // Build the query string for backend calls. Empty string = no filter.
  const rangeQS = useMemo(() => {
    const params = new URLSearchParams();
    if (dateFilter?.start) params.set("start", new Date(dateFilter.start).toISOString());
    if (dateFilter?.end) params.set("end", new Date(dateFilter.end).toISOString());
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [dateFilter]);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/timeclock/summary${rangeQS}`);
      setSummary(res.data);
      // Reset cached per-user logs since the range changed
      setUserLog({});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [rangeQS]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live ticker — refreshes every second so elapsed shows to the second
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const clockIn = async (userId) => {
    setProcessing(userId);
    try {
      await api.post("/timeclock/clock-in", { user_id: userId });
      toast.success("Clocked in");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setProcessing(null); }
  };

  const clockOut = async (userId) => {
    setProcessing(userId);
    try {
      await api.post("/timeclock/clock-out", { user_id: userId });
      toast.success("Clocked out");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setProcessing(null); }
  };

  const buildEntriesUrl = (userId) => {
    const params = new URLSearchParams({ user_id: userId, limit: "500" });
    if (dateFilter?.start) params.set("start", new Date(dateFilter.start).toISOString());
    if (dateFilter?.end) params.set("end", new Date(dateFilter.end).toISOString());
    return `/timeclock/entries?${params.toString()}`;
  };

  const toggleLog = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(userId);
    if (!userLog[userId]) {
      try {
        const res = await api.get(buildEntriesUrl(userId));
        setUserLog(p => ({ ...p, [userId]: res.data || [] }));
      } catch { toast.error("Failed to load log"); }
    }
  };

  const refreshLog = async (userId) => {
    try {
      const res = await api.get(buildEntriesUrl(userId));
      setUserLog(p => ({ ...p, [userId]: res.data || [] }));
    } catch (err) { console.warn("Failed to refresh timeclock log", err); }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry.entry_id);
    setEditForm({
      clock_in: isoToLocalInput(entry.clock_in),
      clock_out: isoToLocalInput(entry.clock_out),
      notes: entry.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setEditForm({ clock_in: "", clock_out: "", notes: "" });
  };

  const saveEdit = async (entry) => {
    const ciIso = localInputToIso(editForm.clock_in);
    if (!ciIso) { toast.error("Invalid clock-in time"); return; }
    const coIso = editForm.clock_out ? localInputToIso(editForm.clock_out) : null;
    if (editForm.clock_out && !coIso) { toast.error("Invalid clock-out time"); return; }
    if (coIso && new Date(coIso) <= new Date(ciIso)) {
      toast.error("Clock-out must be after clock-in"); return;
    }
    setEditSaving(true);
    try {
      await api.put(`/timeclock/entries/${entry.entry_id}`, {
        clock_in: ciIso,
        clock_out: coIso,
        notes: editForm.notes,
      });
      toast.success("Entry updated");
      cancelEdit();
      await refreshLog(entry.user_id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setEditSaving(false);
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm("Delete this time entry? This cannot be undone.")) return;
    try {
      await api.delete(`/timeclock/entries/${entry.entry_id}`);
      toast.success("Entry deleted");
      await refreshLog(entry.user_id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  // CSV export — uses whatever range is currently active. We fetch all
  // entries (across all staff) in the range from the backend so the file
  // is comprehensive even if the user hasn't expanded individual logs.
  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ limit: "5000" });
      if (dateFilter?.start) params.set("start", new Date(dateFilter.start).toISOString());
      if (dateFilter?.end) params.set("end", new Date(dateFilter.end).toISOString());
      // Fetch entries + the full staff roster (including inactive) in
      // parallel so deactivated team-members still resolve to a real name.
      const [entriesRes, staffRes] = await Promise.all([
        api.get(`/timeclock/entries?${params.toString()}`),
        api.get(`/staff/all?include_inactive=true`).catch(() => ({ data: [] })),
      ]);
      const entries = entriesRes.data || [];
      const userMap = {};
      for (const u of (staffRes.data || [])) {
        userMap[u.user_id] = { name: u.name, role: u.role };
      }
      // Layer in summary data on top in case /staff/all is restricted
      for (const s of summary) {
        userMap[s.user_id] = userMap[s.user_id] || { name: s.name, role: s.role };
      }
      const escape = (v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = [
        ["Staff", "Role", "Date", "Clock In", "Clock Out", "Hours", "Location", "Notes", "Edited"],
      ];
      for (const e of entries) {
        const u = userMap[e.user_id] || { name: "Unknown staff", role: "" };
        const ci = e.clock_in ? new Date(e.clock_in) : null;
        const co = e.clock_out ? new Date(e.clock_out) : null;
        rows.push([
          u.name,
          u.role,
          ci ? ci.toLocaleDateString("en-US") : "",
          ci ? ci.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "",
          co ? co.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "(open)",
          e.total_hours != null ? Number(e.total_hours).toFixed(2) : "",
          e.location_name || "",
          e.notes || "",
          e.edited_at ? "yes" : "",
        ]);
      }
      // Append per-staff summary block
      rows.push([]);
      rows.push(["TOTALS BY STAFF"]);
      rows.push(["Staff", "Role", "Hours in range", "Entries"]);
      for (const s of summary) {
        rows.push([
          s.name,
          s.role,
          Number(s.range_hours ?? s.week_hours ?? 0).toFixed(2),
          (s.range_entries || s.recent_entries || []).length,
        ]);
      }
      const csv = rows.map(r => r.map(escape).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const labelSafe = (dateFilter?.label || "range").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      a.download = `timeclock_${labelSafe}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${entries.length} entries`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const clockedIn = summary.filter(s => s.clocked_in);
  const clockedOut = summary.filter(s => !s.clocked_in);

  return (
    <div className={embedded ? "" : "p-6 lg:p-8 max-w-[1600px] mx-auto"} data-testid="timeclock-page">
      <div className={`flex items-center ${embedded ? "justify-end" : "justify-between"} mb-8 flex-wrap gap-3`}>
        {!embedded && (
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Time Clock</h1>
            <p className="text-slate-500 text-sm mt-1 font-['Manrope']">Track team clock-in and clock-out times, down to the minute</p>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter
            value={dateFilter}
            onChange={setDateFilter}
            data-testid="timeclock-date-filter"
          />
          <Button variant="outline" onClick={exportCsv} className="border-slate-200 rounded-full h-10 px-5 text-sm" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" />Export CSV
          </Button>
          {user?.role === "owner" && (
            <Button variant="outline" onClick={() => setChangelogOpen(true)}
              data-testid="open-changelog-btn"
              className="border-slate-200 rounded-full h-10 px-5 text-sm">
              <History className="w-4 h-4 mr-1.5" />Audit Log
            </Button>
          )}
          <Button variant="outline" onClick={fetchData} className="border-slate-200 rounded-full h-10 px-5 text-sm" data-testid="refresh-timeclock">
            <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
          </Button>
        </div>
      </div>

      {/* Active range banner — shows what date window is currently in effect */}
      <div className="mb-4 flex items-center gap-2 text-xs text-slate-500 font-['Manrope']" data-testid="active-range-banner">
        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
        <span>Showing entries for: <span className="font-semibold text-slate-700">{dateFilter?.label || "All time"}</span></span>
        {dateFilter?.start && dateFilter?.end && (
          <span className="text-slate-400">
            ({new Date(dateFilter.start).toLocaleDateString()} – {new Date(dateFilter.end).toLocaleDateString()})
          </span>
        )}
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-6">
          <Clock className="w-5 h-5 text-rose-400 mb-2" strokeWidth={1.5} />
          <p className="font-['Outfit'] text-4xl font-semibold text-slate-900">{summary.length}</p>
          <p className="text-sm text-slate-400 mt-1 font-['Manrope']">Team Members</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-6">
          <LogIn className="w-5 h-5 text-emerald-600 mb-2" strokeWidth={1.5} />
          <p className="font-['Outfit'] text-4xl font-semibold text-emerald-600">{clockedIn.length}</p>
          <p className="text-sm text-slate-400 mt-1 font-['Manrope']">Clocked In</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-6">
          <LogOut className="w-5 h-5 text-slate-400 mb-2" strokeWidth={1.5} />
          <p className="font-['Outfit'] text-4xl font-semibold text-slate-400">{clockedOut.length}</p>
          <p className="text-sm text-slate-400 mt-1 font-['Manrope']">Clocked Out</p>
        </div>
      </div>

      {/* Kiosk Pairing (managers + owners only) */}
      {(user?.role === "owner" || user?.role === "manager") && (
        <div className="mb-8">
          <KioskPairingCard />
        </div>
      )}

      {/* Missed Punch Requests (managers + owners only) */}
      {(user?.role === "owner" || user?.role === "manager") && (
        <MissedPunchManager onAfterAction={fetchData} />
      )}

      {/* Time-Off Requests (managers + owners only) — hidden when embedded
          in the merged Timesheets page, which has a dedicated Time Off tab */}
      {!embedded && (user?.role === "owner" || user?.role === "manager") && (
        <TimeOffManager />
      )}

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="timeclock-grid">
        {summary.map(s => {
          const initials = s.name?.split(" ").map(n => n[0]).join("") || "?";
          const isProcessing = processing === s.user_id;
          const isExpanded = expandedUser === s.user_id;
          const log = userLog[s.user_id] || s.recent_entries || [];

          // Live elapsed time for the current open session
          const elapsedMs = s.clocked_in && s.current_entry
            ? msBetween(s.current_entry.clock_in, null)
            : 0;
          // eslint-disable-next-line no-unused-vars
          const _tick = now; // ensure re-render every second

          return (
            <div key={s.user_id}
              className={`bg-white border rounded-2xl p-5 transition-all ${s.clocked_in ? "border-emerald-200 shadow-[0_2px_12px_rgba(16,185,129,0.08)]" : "border-slate-100"}`}
              data-testid={`timeclock-card-${s.user_id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white" style={{ backgroundColor: s.color }}>
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{s.name}</p>
                    <p className="text-xs text-slate-400 capitalize font-['Manrope']">{s.role?.replace("_", " ")}</p>
                  </div>
                </div>
                <div className={`w-2.5 h-2.5 rounded-full ${s.clocked_in ? "bg-emerald-500 animate-pulse" : "bg-slate-200"}`} title={s.clocked_in ? "Clocked In" : "Clocked Out"} />
              </div>

              {s.clocked_in && s.current_entry && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3.5 py-2.5 mb-3" data-testid={`live-session-${s.user_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 font-['Manrope']">On the clock</p>
                    <p className="text-[10px] text-emerald-600 font-mono tabular-nums font-semibold" data-testid={`elapsed-${s.user_id}`}>
                      {formatElapsed(elapsedMs)}
                    </p>
                  </div>
                  <p className="text-xs text-emerald-700 font-['Manrope'] mt-0.5">
                    Since {formatTime(s.current_entry.clock_in)}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-slate-50/80 rounded-xl px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope']" title={dateFilter?.label}>
                    {dateFilter?.label === "All time" ? "All time" : dateFilter?.label || "This week"}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 font-['Outfit'] tabular-nums" data-testid={`week-hours-${s.user_id}`}>
                    {formatHoursMinutes(s.range_hours ?? s.week_hours)}
                  </p>
                </div>
                <div className="bg-slate-50/80 rounded-xl px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold font-['Manrope']">Last shift</p>
                  <p className="text-sm font-semibold text-slate-900 font-['Outfit'] tabular-nums">
                    {(() => {
                      const last = (s.recent_entries || []).find(e => e.clock_out);
                      return last ? formatHoursMinutes(last.total_hours) : "—";
                    })()}
                  </p>
                </div>
              </div>

              <Button
                data-testid={`clock-${s.clocked_in ? "out" : "in"}-${s.user_id}`}
                onClick={() => s.clocked_in ? clockOut(s.user_id) : clockIn(s.user_id)}
                disabled={isProcessing}
                className={`w-full rounded-full h-10 text-sm font-semibold ${
                  s.clocked_in
                    ? "bg-rose-500 text-white hover:bg-rose-600"
                    : "bg-emerald-500 text-white hover:bg-emerald-600"
                }`}
              >
                {isProcessing ? <Timer className="w-4 h-4 mr-1.5 animate-spin" /> : s.clocked_in ? <LogOut className="w-4 h-4 mr-1.5" /> : <LogIn className="w-4 h-4 mr-1.5" />}
                {isProcessing ? "Processing..." : s.clocked_in ? "Clock Out" : "Clock In"}
              </Button>

              {/* Add manual shift — managers & owners only. Sits right
                  under the Clock toggle so it's discoverable without
                  needing to expand the log or bounce to Payroll. */}
              {(user?.role === "owner" || user?.role === "manager") && (
                <button
                  onClick={() => setAddShiftFor({ user_id: s.user_id, name: s.name })}
                  data-testid={`add-shift-${s.user_id}`}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-rose-600 rounded-full h-8 border border-slate-200 hover:border-rose-200 hover:bg-rose-50 transition-colors font-['Manrope']"
                >
                  <Plus className="w-3 h-3" /> Add manual shift
                </button>
              )}

              {/* Expand/Collapse full log */}
              <button
                onClick={() => toggleLog(s.user_id)}
                data-testid={`toggle-log-${s.user_id}`}
                className="mt-3 w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-colors py-1.5 font-['Manrope']"
              >
                <CalendarDays className="w-3 h-3" />
                {isExpanded ? "Hide log" : "View full log"}
                <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
              </button>

              {/* Full log table */}
              {isExpanded && (
                <div className="mt-2 pt-3 border-t border-slate-100 space-y-1 max-h-[320px] overflow-y-auto" data-testid={`log-${s.user_id}`}>
                  {log.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3 font-['Manrope']">No entries yet</p>
                  ) : (
                    log.map(e => {
                      const active = !e.clock_out;
                      const dur = active
                        ? msBetween(e.clock_in, null) / 3600000
                        : e.total_hours;
                      const isEditing = editingEntry === e.entry_id;

                      if (isEditing) {
                        return (
                          <div key={e.entry_id} className="bg-rose-50/40 border border-rose-200 rounded-xl p-3 space-y-2" data-testid={`edit-form-${e.entry_id}`}>
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-['Manrope']">Clock in</label>
                              <input type="datetime-local" value={editForm.clock_in}
                                onChange={ev => setEditForm(f => ({ ...f, clock_in: ev.target.value }))}
                                data-testid={`edit-clock-in-${e.entry_id}`}
                                className="w-full mt-0.5 h-8 px-2 rounded-lg border border-slate-200 text-xs font-['Manrope'] focus:outline-none focus:border-rose-400" />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-['Manrope']">Clock out {active && <span className="text-emerald-600 ml-1">(leave blank to keep open)</span>}</label>
                              <input type="datetime-local" value={editForm.clock_out}
                                onChange={ev => setEditForm(f => ({ ...f, clock_out: ev.target.value }))}
                                data-testid={`edit-clock-out-${e.entry_id}`}
                                className="w-full mt-0.5 h-8 px-2 rounded-lg border border-slate-200 text-xs font-['Manrope'] focus:outline-none focus:border-rose-400" />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-['Manrope']">Note</label>
                              <input type="text" value={editForm.notes}
                                onChange={ev => setEditForm(f => ({ ...f, notes: ev.target.value }))}
                                placeholder="Reason for edit…"
                                data-testid={`edit-notes-${e.entry_id}`}
                                className="w-full mt-0.5 h-8 px-2 rounded-lg border border-slate-200 text-xs font-['Manrope'] focus:outline-none focus:border-rose-400" />
                            </div>
                            <div className="flex items-center justify-end gap-1.5 pt-1">
                              <button onClick={cancelEdit} data-testid={`edit-cancel-${e.entry_id}`}
                                className="h-7 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-600 font-['Manrope']">
                                Cancel
                              </button>
                              <button onClick={() => saveEdit(e)} disabled={editSaving} data-testid={`edit-save-${e.entry_id}`}
                                className="h-7 px-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-semibold font-['Manrope'] flex items-center gap-1 disabled:opacity-50">
                                <Check className="w-3 h-3" />
                                {editSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={e.entry_id}
                          className={`group flex items-center justify-between py-2 px-2.5 rounded-lg text-[12px] font-['Manrope'] tabular-nums ${
                            active ? "bg-emerald-50/60" : "hover:bg-slate-50"
                          }`}
                          data-testid={`log-entry-${e.entry_id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold">{formatDate(e.clock_in)}</p>
                              {e.edited_at && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase tracking-wider">Edited</span>}
                            </div>
                            <p className="text-slate-800 font-medium mt-0.5">
                              {formatTime(e.clock_in)}<span className="text-slate-300 mx-1">→</span>{active ? <span className="text-emerald-600 font-semibold">now</span> : formatTime(e.clock_out)}
                            </p>
                            {e.location_name && (
                              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1" data-testid={`log-entry-location-${e.entry_id}`}>
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400" />
                                {e.location_name}
                              </p>
                            )}
                            {(e.breaks || []).length > 0 && (
                              <div className="mt-1 space-y-0.5" data-testid={`log-entry-breaks-${e.entry_id}`}>
                                {e.breaks.map((b, bi) => (
                                  <p key={b.break_id || bi} className="text-[10px] text-amber-600 flex items-center gap-1">
                                    <Coffee className="w-2.5 h-2.5 flex-shrink-0" />
                                    Break {formatTime(b.start)}
                                    <span className="text-amber-400">→</span>
                                    {b.end ? formatTime(b.end) : "now"}
                                    {b.duration_hours != null && <span className="font-semibold">({formatHoursMinutes(b.duration_hours)})</span>}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className={`text-sm font-semibold flex-shrink-0 ml-3 ${active ? "text-emerald-600" : "text-slate-900"}`}>
                            {formatHoursMinutes(dur)}
                          </p>
                          {canEdit && (
                            <div className="flex items-center gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => startEdit(e)} data-testid={`edit-entry-${e.entry_id}`}
                                title="Edit entry"
                                className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors">
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button onClick={() => deleteEntry(e)} data-testid={`delete-entry-${e.entry_id}`}
                                title="Delete entry"
                                className="p-1 rounded hover:bg-rose-100 text-slate-400 hover:text-rose-500 transition-colors">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TimeClockChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />

      {addShiftFor && (
        <AddShiftModal
          staffId={addShiftFor.user_id}
          staffName={addShiftFor.name}
          onClose={() => setAddShiftFor(null)}
          onSaved={async () => {
            setAddShiftFor(null);
            // Refresh both the summary and the per-staff log if it
            // happens to be expanded, so the new entry appears
            // immediately without a full reload.
            await fetchData();
            if (expandedUser === addShiftFor.user_id) {
              try {
                const res = await api.get(`/timeclock/entries?user_id=${addShiftFor.user_id}`);
                setUserLog(prev => ({ ...prev, [addShiftFor.user_id]: res.data || [] }));
              } catch { /* keep stale log if reload fails */ }
            }
          }}
        />
      )}
    </div>
  );
}
